import {escapeHtml} from 'shared/utils';
import {normalizeBoolValue} from 'utils/common';

import {fetchPreviewPdfSetting} from '../api/api';

export const PdfPreviewService = {
    /**
     * @description
     * Вспомогательный метод для безопасной работы с временными ссылками на Blob-объекты.
     * Создаёт `blob:` URL из переданного Blob, передаёт его в колбэк для дальнейшего использования,
     * а затем гарантированно освобождает память, удаляя временный URL после завершения работы.
     *
     * @param {Blob} pdfBlob - Объект Blob, содержащий данные PDF-файла.
     * @param {(blobUrl: string) => void} action - Функция-колбэк, в которую передаётся созданный blob URL.
     *   Внутри неё можно использовать ссылку для любых действий (открытие в новом окне, загрузка файла и т. д.).
     *
     * @returns {void}
     * @remarks
     * - После выполнения `action` ссылка будет автоматически отозвана с помощью `URL.revokeObjectURL`,
     *   чтобы предотвратить утечки памяти.
     * - Задержка в `setTimeout` используется для корректного завершения всех операций с URL
     *   перед его освобождением.
     */
    withBlobUrl: (pdfBlob: Blob, action: (blobUrl: string) => void) => {
        const blobUrl = URL.createObjectURL(pdfBlob);

        try {
            action(blobUrl);
        } finally {
            // Чистим ссылку, чтобы не было утечек памяти
            setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
        }
    },

    /**
     * @description
     * Автоматически запускает скачивание PDF с указанным именем файла.
     *
     * @param {Blob} pdfBlob - Объект Blob, содержащий данные PDF-файла.
     * @param {string} fileName - Имя файла, под которым будет сохранён PDF.
     *
     * @returns {void}
    */
    downloadPdf: (pdfBlob: Blob, fileName: string): void => {
        const safeFileName = escapeHtml(fileName);

        PdfPreviewService.withBlobUrl(pdfBlob, blobUrl => {
            const downloadLink = document.createElement('a');
            downloadLink.href = blobUrl;
            downloadLink.download = safeFileName;
            downloadLink.rel = 'noopener noreferrer';
            downloadLink.style.display = 'none';

            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        });
    },

    /**
    * @description
     * Функция выполняет асинхронный запрос для получения настройки предпросмотра PDF.
     * Полученное значение нормализуется из формата 'Y' | 'N' в boolean.
     * Если запрос падает с ошибкой, то функция по-умолчанию вернет true.
     *
     * @returns {Promise<boolean>} Promise с булевым значением, указывающим включен ли предпросмотр PDF
    */
    getPreviewPdfSetting: async (): Promise<boolean> => {
      try {
        const {data} = await fetchPreviewPdfSetting()
        return normalizeBoolValue(data);
      } catch (e) {
        console.error('Произошла ошибка при получении настройки предпросмотра PDF: ', e);
        return true;
      }
    },

    /**
     * @description
     * Открывает новый браузерный таб для отображения PDF.
     *
     * @param {Blob} pdfBlob - Объект Blob, содержащий данные PDF-файла.
     *
     * @returns {void}
    */
    openPreviewWindow: (pdfBlob: Blob): void => {
        PdfPreviewService.withBlobUrl(pdfBlob, blobUrl => {
            const previewWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');
            if (!previewWindow) {
                console.error('Не удалось открыть новую вкладку для предпросмотра PDF');
            }
        });
    },

    /**
     * @description
     * Комбинированный метод: запускает автоматическое скачивание PDF и открывает новое окно для предпросмотра
     *
     * @param {Blob} pdfBlob - Объект Blob, содержащий данные PDF-файла.
     * @param {string} fileName - Имя файла, под которым будет сохранён PDF.
     *
     * @returns {void}
    */
    downloadAndOpenPdf: (pdfBlob: Blob, fileName: string): void => {
        PdfPreviewService.downloadPdf(pdfBlob, fileName);
        PdfPreviewService.openPreviewWindow(pdfBlob);
    },
};
